import { observer } from 'mobx-react-lite'
import clsx from 'clsx';
import { useRunState } from '@/src/game/provider/useRunState';
import { useSteleBreakpoint } from '@/src/hooks/useBreakpoint';
import { useEffect, useRef, useState } from 'react';
import { motion } from 'motion/react'
import { maxLifes } from '@/src/game/config/constants';
import { LevelProgressDisplay } from './LevelProgressDisplay';
import { store } from '@/src/stores/store';
import { LEVELS } from '@/src/game/config/levels';
import IconLockDark from '../icons/IconLockDark';
import { UpgradeSequenceOverlay } from './UpgradeSequenceOverlay';
import IconCheck from '../icons/IconCheck';

const DisplayElement = observer(function DisplayElement() {
    const runState = useRunState()
    const snapshot = runState.snapshot

    const shieldHeart = '/images/hearts/Shielded.png'
    const redHeart = '/images/hearts/Default.png'
    const brokenHeart = '/images/hearts/Empty.png'

    const isStele = useSteleBreakpoint();
    const bubbleRef = useRef<HTMLImageElement>(null)

    const heartVerticalOffsets = isStele ? [48, 12, 0, 12, 48] : [8, 2, 0, 2, 8]

    const isStarPowerUpActive = snapshot.planeTimer > 0

    const nextUpgradeItem = store.unlockedGearLevel === 0 && 'boots' || store.unlockedGearLevel === 1 && 'gloves' || store.unlockedGearLevel === 2 && 'helmet' || store.unlockedGearLevel === 3 && 'vest'
    const levelRange = LEVELS[store.unlockedGearLevel]
    const unlockTarget = levelRange
        ? levelRange.end
        : 0

    const coinCount = snapshot.runEndStats.coinPowerUpsCollected
    const lifeCount = snapshot.lifes
    const shieldCollectCount = snapshot.runEndStats.shieldPowerUpsCollected
    const previousCoinCountRef = useRef(coinCount)
    const previousLifeCountRef = useRef(lifeCount)
    const previousShieldCollectCountRef = useRef(shieldCollectCount)
    const previousShieldActiveRef = useRef(snapshot.shieldActive)
    const previousStarPowerUpActiveRef = useRef(isStarPowerUpActive)
    const [coinAnimationKey, setCoinAnimationKey] = useState(0)
    const [heartAnimationKey, setHeartAnimationKey] = useState(0)
    const [shieldHeartAnimationKey, setShieldHeartAnimationKey] = useState(0)
    const [starAnimationKey, setStarAnimationKey] = useState(0)
    const [animatedHeartIndex, setAnimatedHeartIndex] = useState<number | null>(null)
    const [animatedShieldHeartIndex, setAnimatedShieldHeartIndex] = useState<number | null>(null)
    const [pendingShieldHeartIndex, setPendingShieldHeartIndex] = useState<number | null>(null)

    useEffect(() => {
        if (coinCount > previousCoinCountRef.current) {
            setCoinAnimationKey((current) => current + 1)
        }

        previousCoinCountRef.current = coinCount
    }, [coinCount])

    useEffect(() => {
        if (lifeCount !== previousLifeCountRef.current) {
            const changedHeartIndex = lifeCount > previousLifeCountRef.current
                ? lifeCount - 1
                : previousLifeCountRef.current - 1

            setAnimatedHeartIndex(changedHeartIndex)
            setHeartAnimationKey((current) => current + 1)
        }

        previousLifeCountRef.current = lifeCount
    }, [lifeCount])

    useEffect(() => {
        if (
            shieldCollectCount > previousShieldCollectCountRef.current &&
            snapshot.lifes > 0 &&
            !previousShieldActiveRef.current
        ) {
            const nextShieldHeartIndex = snapshot.lifes - 1
            setPendingShieldHeartIndex(nextShieldHeartIndex)

            const timeoutId = window.setTimeout(() => {
                setAnimatedShieldHeartIndex(nextShieldHeartIndex)
                setShieldHeartAnimationKey((current) => current + 1)
                setPendingShieldHeartIndex(null)
            }, 1000)

            previousShieldCollectCountRef.current = shieldCollectCount
            previousShieldActiveRef.current = snapshot.shieldActive

            return () => window.clearTimeout(timeoutId)
        }

        previousShieldCollectCountRef.current = shieldCollectCount
        previousShieldActiveRef.current = snapshot.shieldActive
    }, [shieldCollectCount, snapshot.lifes, snapshot.shieldActive])

    useEffect(() => {
        if (isStarPowerUpActive && !previousStarPowerUpActiveRef.current) {
            setStarAnimationKey((current) => current + 1)
        }

        previousStarPowerUpActiveRef.current = isStarPowerUpActive
    }, [isStarPowerUpActive])

    return (
        <>
            <div className={clsx(
                'z-50 absolute flex flex-col justify-center items-center w-full',
                'top-6 left-0 ',
                'stele:top-32 stele:left-0'
            )}>
                <div className='relative'>
                    {/* ----------------------- Background ----------------------- */}
                    <svg width={isStele ? '1988' : '332'} height={isStele ? '481' : '86'} viewBox='0 0 1988 481' fill='none' xmlns='http://www.w3.org/2000/svg'>
                        <path d='M1987.2 251.09C1986.39 377.649 1883.55 479.999 1756.8 479.999C1663.83 479.999 1583.78 424.931 1547.37 345.757C1526.08 299.46 1500.21 264.111 1464.66 255.495C1309.9 217.993 1151.76 199.231 993.6 199.199C835.444 199.231 677.299 218.003 522.54 255.508C486.986 264.124 461.116 299.474 439.828 345.771C403.423 424.944 323.372 480.013 230.399 480.013C103.154 480.012 0.00126328 376.858 0 249.613C0 122.367 103.153 19.2141 230.399 19.2139C272.508 19.2139 312.056 30.5371 346.074 50.3232C385.831 73.447 424.801 87.004 459.241 78.3271C634.595 34.1476 814.09 12.0365 993.594 12H993.605C1173.11 12.0364 1352.6 34.1374 1527.96 78.3145C1562.4 86.9908 1601.37 73.4333 1641.13 50.3096C1675.14 30.5237 1714.69 19.2002 1756.8 19.2002C1884.05 19.2002 1987.2 122.353 1987.2 249.6L1987.2 251.09Z' fill='#5486AC' />
                        <path d='M1987.2 239.09C1986.39 365.649 1883.55 467.999 1756.8 467.999C1663.83 467.999 1583.78 412.931 1547.37 333.757C1526.08 287.46 1500.21 252.111 1464.66 243.495C1309.9 205.993 1151.76 187.231 993.6 187.199C835.444 187.231 677.299 206.003 522.54 243.508C486.986 252.124 461.116 287.474 439.828 333.771C403.423 412.944 323.372 468.013 230.399 468.013C103.154 468.012 0.00126328 364.858 0 237.613C0 110.367 103.153 7.21408 230.399 7.21387C272.508 7.21387 312.056 18.5371 346.074 38.3232C385.831 61.447 424.801 75.004 459.241 66.3271C634.595 22.1476 814.09 0.0364523 993.594 0H993.605C1173.11 0.0364323 1352.6 22.1374 1527.96 66.3145C1562.4 74.9908 1601.37 61.4333 1641.13 38.3096C1675.14 18.5237 1714.69 7.20022 1756.8 7.2002C1884.05 7.2002 1987.2 110.353 1987.2 237.6L1987.2 239.09Z' fill='url(#paint0_linear_8465_36538)' />
                        <circle opacity='0.35' cx='234' cy='238' r='180' fill='#06487C' />
                        <circle opacity='0.35' cx='1757' cy='238' r='180' fill='#06487C' />
                        <defs>
                            <linearGradient id='paint0_linear_8465_36538' x1='993.603' y1='468.013' x2='993.603' y2='-5.19898e-07' gradientUnits='userSpaceOnUse'>
                                <stop stop-color='#C8E3F7' />
                                <stop offset='1' stop-color='white' />
                            </linearGradient>
                        </defs>
                    </svg>


                    <LevelProgressDisplay />

                    {/* ----------------------- PowerUps Display ----------------------- */}
                    <div
                        className={clsx(
                            'absolute flex flex-col justify-center items-center',
                            'top-2.5 left-1.75 w-16 h-16',
                            'stele:top-9 stele:left-8 stele:w-100 stele:h-100'
                        )}
                    >
                        <motion.img
                            key={coinAnimationKey}
                            src='/images/PowerUpCoin.png'
                            alt='Powerup Coin'
                            className='w-full h-full'
                            initial={{ scale: 1 }}
                            animate={{ scale: [1, 1.22, 1] }}
                            transition={{
                                duration: 0.28,
                                ease: 'easeOut',
                            }}
                        />
                        <div
                            className={clsx(
                                'absolute rounded-full',
                                '-bottom-2.5 w-[33.5px] h-4.5',
                                'stele:-bottom-10 stele:w-50.5 stele:h-26'
                            )}
                        >
                            <div className='drop-shadow-1-yellow-stroke border border-yellow-stroke stele:border-8 rounded-full w-full h-full'>
                                <div className='flex justify-center items-center bg-golden-gradient rounded-full w-full h-full'>
                                    <p
                                        className={clsx(
                                            'font-nunito font-black text-yellow-stroke italic leading-[100%] tracking-[2%]',
                                            'text-[12px]',
                                            'stele:text-[72px]'
                                        )}
                                    >
                                        {coinCount}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div
                        className={clsx(
                            'z-50 absolute flex flex-col justify-center items-center rounded-full',
                            'top-1.5 right-0.75 w-18 h-18',
                            'stele:top-10 stele:right-6 stele:w-100 stele:h-100',
                            store.unlockedGearLevel < 4 ? 'p-0 stele:p-0' : 'p-2.5 stele:p-10'
                        )}
                    >
                        {store.unlockedGearLevel < 4 ?
                            <>
                                <motion.img
                                    ref={bubbleRef}
                                    src='/images/Bubble_Idle.png'
                                    alt=''
                                    aria-hidden
                                    className='w-full h-full'
                                    animate={{
                                        rotate: [20, -20],
                                        scaleX: [0.9, 1.06],
                                        scaleY: [1.06, 0.9],
                                    }}
                                    transition={{
                                        duration: 1.75,
                                        repeat: Infinity,
                                        ease: 'easeInOut',
                                        repeatType: 'reverse',
                                    }}
                                />
                                <img src={`/images/editor/${nextUpgradeItem}.png`} alt='item' className='absolute max-w-12 stele:max-w-48 max-h-12 stele:max-h-48' />
                                <span
                                    className={clsx(
                                        'absolute flex justify-center items-center font-bold text-white select-none',
                                        'text-sm w-8 h-8 -bottom-1.5',
                                        'stele:w-32 stele:h-32 stele:text-[64px] stele:-bottom-6',
                                    )}
                                    aria-hidden
                                >
                                    <p
                                        className={clsx(
                                            'absolute font-grandstander font-black text-white italic leading-[100%] tracking-[2%]',
                                            'text-[12px] bottom-0.75',
                                            'stele:text-[72px] stele:bottom-0'
                                        )}
                                    >
                                        {unlockTarget}
                                    </p>
                                    <IconLockDark width={isStele ? '230' : '25'} height={isStele ? '230' : '33'} />
                                </span>
                            </>
                            :
                            <div className='relative flex justify-center items-center bg-orange-gradient border-2 border-orange-stroke rounded-full w-full h-full'>
                                <div className='top-0.75 stele:top-3 absolute bg-shine-gradient rounded-full w-5.25 stele:w-21 h-5.25 stele:h-21' />
                                <IconCheck width={isStele ? '117' : '29'} height={isStele ? '117' : '29'} />
                            </div>
                        }
                    </div>
                </div>
                {/* ----------------------- Hearts Display ----------------------- */}
                <div
                    className={clsx(
                        'z-50 relative flex justify-center items-center w-full',
                        '-top-10',
                        'stele:-top-50'
                    )}
                >
                    <div
                        className={clsx(
                            'flex justify-center items-start',
                            'gap-0',
                            'stele:gap-6 '
                        )}
                    >
                        {Array.from({ length: maxLifes }, (_, index) => {
                            const isFilled = index < snapshot.lifes
                            const isShielded =
                                snapshot.shieldActive &&
                                snapshot.lifes > 0 &&
                                index === snapshot.lifes - 1
                            const showShieldHeart =
                                isShielded && pendingShieldHeartIndex !== index
                            const shouldAnimateShieldHeart =
                                showShieldHeart &&
                                index === animatedShieldHeartIndex
                            const verticalOffset = heartVerticalOffsets[index] ?? 0

                            return (
                                <div
                                    key={index}
                                    className={[
                                        'flex items-center justify-center transition-transform',
                                        'text-[36px] h-9 w-9',
                                        'stele:w-43.25 stele:h-43.25 stele:text-[228px]',
                                    ].join(' ')}
                                    style={{
                                        transform: `translateY(${verticalOffset}px)`,
                                    }}
                                >
                                    <motion.img
                                        key={
                                            shouldAnimateShieldHeart
                                                ? `shield-${shieldHeartAnimationKey}-${index}`
                                                : index === animatedHeartIndex
                                                    ? `${heartAnimationKey}-${index}-${isFilled ? 'filled' : 'broken'}-${showShieldHeart ? 'shielded' : 'plain'}`
                                                    : `${index}-${isFilled ? 'filled' : 'broken'}-${showShieldHeart ? 'shielded' : 'plain'}`
                                        }
                                        src={showShieldHeart ? shieldHeart : isFilled ? redHeart : brokenHeart}
                                        alt=''
                                        className={clsx(
                                            'w-full h-full object-contain',
                                        )}
                                        initial={{ scale: 1, y: 0 }}
                                        animate={
                                            shouldAnimateShieldHeart ? { scale: [0, 1.2, 1], y: [0, -4, 0] }
                                                :
                                                index === animatedHeartIndex
                                                    ? { scale: [1, 1.16, 0.96, 1], y: [0, -4, 0] }
                                                    : { scale: 1, y: 0 }
                                        }
                                        transition={{
                                            duration: 0.38,
                                            ease: 'easeOut',
                                        }}
                                    />
                                </div>
                            )
                        })}
                    </div>
                </div>
            </div>
            <UpgradeSequenceOverlay bubbleRef={bubbleRef} />
        </>
    )
})

export default DisplayElement
